import { clsx } from "clsx";
import { type FC, type ReactNode, useRef } from "react";

import { useOverlay } from "../../hooks/useOverlay.ts";
import { DialogComponent } from "../Dialog/Dialog.tsx";
import { Label } from "../Label/Label.tsx";
import { Popover } from "../Popover/Popover.tsx";
import { VStack } from "../Stack/VStack.tsx";
import { TriggerButton } from "../TriggerButton/TriggerButton.tsx";
import style from "./HeaderInfoAttributes.module.css";

export interface AttributeProps {
  label: string;
  description?: ReactNode;
  width?: number;
}

/**
 * Generates a horizontal list where each element is a label-value pair.
 * Each label-value pair is stacked vertically:
 *
 *   Labels:    GENE NAME    GENE TYPE    ...
 *   Values:    FumA         mRNA         ...
 */
const Attribute: FC<AttributeProps> = (props) => {
  if (!props.description) return null;

  return (
    <VStack
      spacing={2}
      className={style.attributes_wrapper}
      style={props.width != null ? { width: props.width, maxWidth: "unset" } : undefined}
    >
      <Label style={{ whiteSpace: "nowrap" }}>{props.label}</Label>
      <div className={style.attributes_subtitle}>{props.description}</div>
    </VStack>
  );
};

// --- Responsive List ---
export interface HeaderInfoAttributesProps {
  attributes: AttributeProps[];
  wrap?: boolean;
  isUiSmall?: boolean;
  showLabel?: boolean;
}

export const HeaderInfoAttributes: FC<HeaderInfoAttributesProps> = (props) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const { overlayProps, state, triggerProps } = useOverlay({});

  return props.isUiSmall ? (
    <>
      <TriggerButton {...triggerProps} icon={"info"} label={"More"} showLabel={props.showLabel} ref={triggerRef} />
      <Popover state={state} {...overlayProps} triggerRef={triggerRef}>
        <DialogComponent closeFn={() => {}}>
          <div className={clsx(style.attributes_list)}>
            {props.attributes
              ?.filter((a) => a)
              .map((attribute: AttributeProps, i) => (
                <Attribute {...attribute} key={`header-attribute-${i}`} />
              ))}
          </div>
        </DialogComponent>
      </Popover>
    </>
  ) : (
    <div className={clsx(style.attributes_list, { [style.attributes_list_wrap]: props.wrap === true })}>
      {props.attributes?.map((attribute, i) => (
        <Attribute {...attribute} key={`header-attribute-${i}`} />
      ))}
    </div>
  );
};
